// The month-and-year window the dashboard reports over — the arithmetic only,
// with no React in it, so it can be tested as the pure function it is.
//
// This mirrors apps/api/src/core/reporting/period.ts. The two are NOT shared
// code: the API's version returns Date bounds for SQL and this one returns
// only the `YYYY-MM` strings the control binds to and the request carries.
// What they MUST agree on is the rule — both bounds or neither, `to` not
// before `from`, at most twelve months inclusive — because the server refuses
// with a 400 whatever the client allows, and a control that let an operator
// submit a window the API rejects would turn a rule into an error message.
//
// UTC throughout, matching the API: a month is a label here, not an instant,
// and reading `new Date()` in local time would show an operator in Auckland a
// different "this month" than the server means.

/** `YYYY-MM`. Two-digit, zero-padded month — `2026-1` is not one. */
const MONTH_RE = /^(\d{4})-(\d{2})$/;

/** Twelve months INCLUSIVE — January through December of one year is exactly
 *  at the cap, matching MAX_MONTHS in the API's parser. */
export const MAX_PERIOD_MONTHS = 12;

export interface MonthRange {
  from: string;
  to: string;
}

/** Months since year 0, the representation in which "reversed" and "how many
 *  months" are plain integer arithmetic with no year-boundary special case. */
function ordinal(year: number, month: number): number {
  return year * 12 + (month - 1);
}

function parse(value: string): { year: number; month: number } | null {
  const m = MONTH_RE.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  // A real month, not merely two digits.
  if (month < 1 || month > 12) return null;
  return { year, month };
}

export function isMonth(value: string): boolean {
  return parse(value) !== null;
}

/** `YYYY-MM` for a Date, read in UTC. */
export function monthOf(d: Date): string {
  return `${String(d.getUTCFullYear()).padStart(4, '0')}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** `YYYY-MM` shifted by `delta` months, rolling the year as needed. Date.UTC
 *  normalises an out-of-range month index, so December + 1 is next January
 *  and January - 1 is last December without a branch. */
export function addMonths(month: string, delta: number): string {
  const p = parse(month);
  if (!p) return month;
  return monthOf(new Date(Date.UTC(p.year, p.month - 1 + delta, 1)));
}

/** How many months the range covers, INCLUSIVE — the figure the cap is
 *  compared against. Negative when the range is reversed. */
export function monthSpan(range: MonthRange): number {
  const f = parse(range.from);
  const t = parse(range.to);
  if (!f || !t) return 0;
  return ordinal(t.year, t.month) - ordinal(f.year, f.month) + 1;
}

export type PeriodProblem = 'invalid_month' | 'reversed' | 'too_long';

/**
 * What is wrong with this range, or null when nothing is. The control
 * disables Apply on a non-null answer and prints the matching hint, so the
 * operator is told which rule they broke before the request is made rather
 * than after the server refuses it.
 */
export function periodProblem(range: MonthRange): PeriodProblem | null {
  if (!isMonth(range.from) || !isMonth(range.to)) return 'invalid_month';
  const span = monthSpan(range);
  if (span < 1) return 'reversed';
  if (span > MAX_PERIOD_MONTHS) return 'too_long';
  return null;
}

/** Three letters each, indexed by month number - 1.
 *
 *  NOT Intl's `month: 'short'`: under `en-GB` that yields "Sept" for
 *  September (a four-letter abbreviation ICU considers correct British
 *  usage) while every other month is three, so the caption's two halves
 *  would not line up and the label would read "Jan 2026 – Sept 2026". The
 *  backoffice is English-only, so a fixed table is honest here and removes
 *  the dependency on which ICU version the browser ships. */
const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** "Jan 2026 – Sep 2026", for the caption under the tiles. A one-month window
 *  still reads as a range ("Sep 2026 – Sep 2026" would be odd), so it
 *  collapses to the single month. */
export function formatPeriodLabel(range: MonthRange): string {
  const label = (month: string): string => {
    const p = parse(month);
    if (!p) return month;
    return `${MONTH_NAMES[p.month - 1]} ${p.year}`;
  };
  if (range.from === range.to) return label(range.from);
  return `${label(range.from)} – ${label(range.to)}`;
}

export type PresetId = 'thisYear' | 'last12' | 'lastMonth' | 'thisMonth';

export interface Preset {
  id: PresetId;
  label: string;
}

export const PRESETS: Preset[] = [
  { id: 'thisYear', label: 'This year' },
  { id: 'last12', label: 'Last 12 months' },
  { id: 'lastMonth', label: 'Last month' },
  { id: 'thisMonth', label: 'This month' },
];

/**
 * The range a preset names, as of `now`.
 *
 * Every one of these is within the twelve-month cap by construction —
 * including "This year" in December, which is exactly twelve — so a preset
 * can never produce a window the Apply button then refuses.
 *
 * @param now injected so the presets are testable without freezing the clock.
 */
export function presetRange(id: PresetId, now: Date = new Date()): MonthRange {
  const current = monthOf(now);
  switch (id) {
    case 'thisYear':
      // January of this year through THIS month — the year to date, which is
      // the same default the API applies when no period is sent.
      return { from: `${String(now.getUTCFullYear()).padStart(4, '0')}-01`, to: current };
    case 'last12':
      // Twelve months INCLUSIVE ending this month, so the offset is 11 —
      // going back 12 would be a thirteen-month window and over the cap.
      return { from: addMonths(current, -(MAX_PERIOD_MONTHS - 1)), to: current };
    case 'lastMonth': {
      const prev = addMonths(current, -1);
      return { from: prev, to: prev };
    }
    case 'thisMonth':
      return { from: current, to: current };
  }
}

/** The window the screen opens on, matching the API's own default so the
 *  first paint's caption describes the figures the server would have sent
 *  with no period at all. */
export function defaultRange(now: Date = new Date()): MonthRange {
  return presetRange('thisYear', now);
}
